import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { RefObject } from "react";

export interface UseMenuPopoverOptions {
  /**
   * Qué se enfoca al abrir, buscado DENTRO del panel.
   *
   * El menú de la cuenta pasa `'[role="menuitem"]'` para caer en la
   * primera acción. Un panel que solo informa —las notificaciones— no
   * pasa nada y entonces se enfoca el panel entero, que para eso lleva
   * `tabIndex={-1}`. Dejar el foco fuera es lo único que no vale: quien
   * abre con el teclado se queda leyendo un panel que no puede recorrer,
   * y Escape se lo lleva al principio del documento.
   */
  focusSelector?: string;
}

export interface MenuPopover {
  open: boolean;
  toggle: () => void;
  /** Cierra sin tocar el foco. Para el clic fuera. */
  close: () => void;
  /** Cierra y devuelve el foco al disparador. Para Escape. */
  closeAndRefocus: () => void;
  triggerRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  /** Para `aria-controls` en el disparador e `id` en el panel. */
  panelId: string;
}

/**
 * El comportamiento de un panel que cuelga de un botón de la barra
 * superior: abrir, cerrar con Escape, cerrar al pulsar fuera y no perder
 * el foco por el camino.
 *
 * ── Por qué es un hook y no se copió ─────────────────────────────────
 * Esto vivía entero dentro de `UserMenu`, y el panel de notificaciones
 * necesitaba exactamente lo mismo. Copiarlo son dos copias de cuatro
 * reglas de accesibilidad finas —y las copias no se arreglan a la vez:
 * la primera vez que alguien corrija el foco en una, la otra se queda
 * con el fallo y nadie se entera hasta que lo sufre una persona que
 * navega con teclado.
 *
 * ── Las reglas, que son el motivo de que exista ──────────────────────
 * · Con **Escape** se cierra y el foco VUELVE al botón que lo abrió, que
 *   es de donde salió. Cerrar sin devolverlo lo manda al principio del
 *   documento, y quien navega con teclado tiene que recorrer la página
 *   entera para volver a donde estaba.
 * · Al cerrar por un **clic fuera** NO se toca el foco: ahí la persona ya
 *   está pulsando otra cosa, y robarle el foco le quitaría ese clic.
 * · Se escucha `pointerdown`/`mousedown` y no `click`: con `click` el
 *   panel sigue abierto durante todo el arrastre y se cierra al soltar,
 *   lo que se ve como un retardo. Se escuchan los DOS porque
 *   `fireEvent.click` de las pruebas no dispara `pointerdown`.
 * · El `stopPropagation` del Escape evita que el mismo tecleo cierre
 *   además el cajón de navegación que hubiera detrás.
 */
export function useMenuPopover({ focusSelector }: UseMenuPopoverOptions = {}): MenuPopover {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => setOpen((current) => !current), []);
  const close = useCallback(() => setOpen(false), []);

  const closeAndRefocus = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Al abrir, el foco entra en el panel.
  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const target = focusSelector
      ? panel.querySelector<HTMLElement>(focusSelector)
      : null;
    (target ?? panel).focus();
  }, [open, focusSelector]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeAndRefocus();
      }
    };

    const onPointerDown = (event: Event) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, closeAndRefocus]);

  return { open, toggle, close, closeAndRefocus, triggerRef, panelRef, panelId };
}
